"use client";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/radix/tooltip";
import { SkeletonLoader } from "@/components/ui/skeleton";
import { Info } from "lucide-react";
import Image from "next/image";

interface SpaceItemProps {
	icon: string;
	label: string;
	available: boolean;
	value: string;
	isLoading?: boolean;
	tooltip?: React.ReactNode;
}

export function SpaceItem({ icon, label, available, value, isLoading = false, tooltip }: SpaceItemProps) {
	return (
		<div className="text-center group">
			<div className="w-20 h-20 bg-gradient-to-br from-accent/5 to-accent/10 rounded-xl flex flex-col items-center justify-center mx-auto mb-4 border border-accent/10 hover:border-accent/20 transition-all duration-200 hover:shadow-sm">
				{/* Icon */}
				<div className="w-full h-8 flex items-center justify-center mb-2 group-hover:opacity-100 transition-opacity duration-200">
					<Image
						src={`/images/spaces/colored/${icon}.svg`}
						alt={label}
						width={24}
						height={24}
						className="text-gray-600"
					/>
				</div>
				{/* Value */}
				{available || !isLoading ? (
					<span className="text-lg font-bold text-primary group-hover:text-primary/90 transition-colors duration-200">
						{value.split(/(sft)/i).map((part, i) =>
							part.toLowerCase() === "sft" ? (
								<span key={i} className="text-[10px] font-semibold ml-0.5 uppercase text-gray-500">
									{part}
								</span>
							) : (
								part
							),
						)}
					</span>
				) : (
					<SkeletonLoader height="16px" width="20px" className="rounded" />
				)}
			</div>
			<div className="flex items-center justify-center gap-1.5">
				<p className="text-sm font-semibold text-gray-700 leading-tight group-hover:text-gray-900 transition-colors duration-200">
					{label}
				</p>
				{tooltip && (
					<Tooltip>
						<TooltipTrigger asChild>
							<Info className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help transition-colors" />
						</TooltipTrigger>
						<TooltipContent className="max-w-[250px] text-justify">{tooltip}</TooltipContent>
					</Tooltip>
				)}
			</div>
		</div>
	);
}
